<template>
    <div class="hidden lg:flex lg:flex-col lg:w-[477px] bg-black border-l border-[#2e2e2e] h-screen overflow-hidden">
        <!-- Top Bar -->
        <div class="px-3 py-3 bg-black flex items-center justify-between shrink-0 border-b border-white/5 gap-2">
            <!-- Left: logo + links - z-[52] so above KebabMenu backdrop and not blurred -->
            <div class="relative flex items-center gap-2 min-w-0" :class="[ requiresAgeVerification ? 'z-[49]' : 'z-[52]' ]">
                <img
                    src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png"
                    alt="Logo"
                    class="h-[2rem] xl:h-[2.5rem] object-contain shrink-0"
                />

                <Link
                    :href="getMainAuditionsUrl(isUserLoggedIn, routePrefix)"
                    class="border border-blue-500/50 bg-blue-500/10 text-blue-400 px-3 xl:px-4 py-1.5 rounded-lg text-[0.6rem] xl:text-[0.65rem] font-semibold transition-all hover:bg-blue-500/20 hover:border-blue-500/70 whitespace-nowrap"
                >
                    AUDITIONS
                </Link>

                <a v-if="!requiresAgeVerification" :href="isUserLoggedIn ? route('members2.main.home') : route('www.main.home')"
                    class="border border-white/15 bg-transparent text-white/60 px-3 xl:px-4 py-1.5 rounded-lg text-[0.6rem] xl:text-[0.65rem] font-medium transition-all hover:border-white/25 hover:bg-white/5 hover:text-white/80 whitespace-nowrap"
                >
                    VIDEOS
                </a>
            </div>
            <!-- Right: JOIN + KebabMenu - JOIN has z-[52] so above backdrop and not blurred; trigger is z-[52] in KebabMenu -->
            <div class="flex items-center gap-2 shrink-0">
                <span v-if="!isUserLoggedIn" class="relative" :class="[ requiresAgeVerification ? 'z-[49]' : 'z-[52]' ]">
                    <button
                        @click="handleGetStarsClick"
                        class="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold px-3 xl:px-4 py-1.5 xl:py-2 rounded-lg text-[0.6rem] xl:text-[0.65rem] transition-all shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30 whitespace-nowrap"
                    >
                        GET STARS
                    </button>
                </span>

                <KebabMenu
                    :isUserLoggedIn="isUserLoggedIn"
                    :isGoldUser="isGoldUser"
                    :starBalance="starBalance"
                    :requiresAgeVerification="requiresAgeVerification"
                    variant="compact"
                    :routePrefix="routePrefix"
                    @joinClick="$emit('joinClick')"
                    @goldJoinClick="$emit('goldJoinClick')"
                />
            </div>
        </div>

        <!-- Main Content Area - Scrollable -->
        <div class="flex-1 bg-[#141414] px-16 overflow-y-auto min-h-0 custom-scrollbar">
            <!-- Context Banner for Guests - Desktop Only (commented out) -->
            <!--
            <div v-if="!isUserLoggedIn" class="py-6">
                <div class="bg-gradient-to-br from-blue-600/10 to-blue-700/10 border border-blue-500/20 rounded-xl p-4">
                    <div class="flex items-start gap-2 mb-3">
                        <Info class="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                        <div>
                            <h4 class="text-white font-bold text-sm mb-1">
                                About Auditions
                            </h4>
                            <p class="text-white/70 text-xs leading-relaxed">
                                Members sponsor auditions with monthly Stars to help decide what fantasies get released.
                            </p>
                        </div>
                    </div>

                    <div class="space-y-1.5 mb-3">
                        <div class="flex items-center gap-2 text-xs">
                            <div class="w-1 h-1 rounded-full bg-blue-400" />
                            <span class="text-white/80">
                                14,000+ full videos included
                            </span>
                        </div>
                        <div class="flex items-center gap-2 text-xs">
                            <div class="w-1 h-1 rounded-full bg-blue-400" />
                            <span class="text-white/80">
                                Monthly Stars to sponsor
                            </span>
                        </div>
                    </div>

                    <button
                        @click="$emit('joinClick')"
                        class="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg transition-colors"
                    >
                        Join to Sponsor
                    </button>
                </div>
            </div>
            -->

            <!-- Sponsorship Widget -->
            <div class="py-6">
                <!-- Circle Progress Ring + Stats -->
                <div class="flex items-center gap-4 mb-5">
                    <!-- Circular Progress Ring -->
                    <div class="relative shrink-0 w-[76px] h-[76px]">
                        <svg class="w-full h-full -rotate-90" viewBox="0 0 84 84">
                            <!-- Track -->
                            <circle
                                cx="42" cy="42" :r="RING_RADIUS"
                                fill="none"
                                stroke="rgba(255,255,255,0.08)"
                                stroke-width="6"
                            />
                            <!-- Progress arc -->
                            <circle
                                cx="42" cy="42" :r="RING_RADIUS"
                                fill="none"
                                stroke="url(#progressGradient)"
                                stroke-width="6"
                                stroke-linecap="round"
                                :stroke-dasharray="RING_CIRCUMFERENCE"
                                :stroke-dashoffset="progressOffset"
                                class="transition-all duration-700 ease-out"
                            />
                            <defs>
                                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stop-color="#2563eb" />
                                    <stop offset="100%" stop-color="#3b82f6" />
                                </linearGradient>
                            </defs>
                        </svg>
                        <!-- Percentage text in center -->
                        <div class="absolute inset-0 flex items-center justify-center">
                            <span class="text-white/90 text-xs font-bold">{{ sponsorPercentage }}%</span>
                        </div>
                    </div>

                    <!-- Stats text -->
                    <div>
                        <h3 class="text-white text-lg font-bold leading-tight">
                            {{ videoData.likes || 0 }} Stars raised
                        </h3>
                        <p class="text-gray-400 text-sm">
                            of {{ formatNumber(videoData.star_goal || 0) }} goal
                        </p>
                    </div>
                </div>

                <!-- Give Stars / Sponsor Button -->
                <button
                    v-if="isUserLoggedIn"
                    @click="handleGiveStarsClick"
                    class="w-full py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold rounded-lg transition-all shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30"
                >
                    Give Stars
                </button>
                <button
                    v-else
                    @click="handleGetStarsClick"
                    class="w-full py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold rounded-lg transition-all shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30"
                >
                    Get Stars to Sponsor
                </button>
            </div>

            <!-- Sponsors List -->
            <div class="py-5 border-t border-white/10">
                <!-- Section heading -->
                <h4 class="text-white font-bold text-sm mb-3">
                    Sponsors{{ sponsorCount > 0 ? ` (${sponsorCount})` : '' }}
                </h4>

                <!-- Empty state -->
                <p v-if="sidebarSponsors.length === 0" class="text-white/50 text-sm py-3">
                    Be the first to sponsor this audition!
                </p>

                <div v-else class="space-y-0">
                    <div
                        v-for="(sponsor, index) in sidebarSponsors"
                        :key="index"
                        class="flex items-center gap-3 py-3 cursor-pointer"
                        :class="{ 'border-t border-white/5': index > 0 }"
                        @click="goToMyProfile(sponsor)"
                    >
                        <!-- Avatar -->
                        <div class="w-9 h-9 rounded-full shrink-0 flex items-center justify-center"
                            :class="index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600' : 'bg-zinc-700'"
                        >
                            <span class="text-white text-sm font-semibold">{{ getSponsorInitial(sponsor) }}</span>
                        </div>

                        <!-- Info -->
                        <div class="min-w-0 flex-1">
                            <p class="text-white text-sm font-medium truncate">{{ sponsor.name }}</p>
                            <div class="flex items-center gap-1.5">
                                <span class="text-gray-400 text-xs font-semibold">{{ sponsor.amount }} Stars</span>
                                <span v-if="sponsor.label" class="text-gray-500 text-xs">
                                    &middot; {{ sponsor.label }}
                                </span>
                            </div>
                        </div>

                        <!-- Star amount (right aligned) -->
                        <div class="flex items-center gap-1 shrink-0">
                            <span class="text-white font-bold text-sm">{{ sponsor.amount }}</span>
                            <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-4 h-4" />
                        </div>
                    </div>
                </div>

                <!-- See all / See top buttons -->
                <div class="flex gap-3 mt-4">
                    <button @click="handleSeeCommentsClick" class="flex-1 py-2.5 border border-white/15 text-white/70 text-sm font-medium rounded-lg hover:bg-white/5 hover:border-white/25 transition-all">
                        See comments
                    </button>
                    <button @click="handleSeeTopClick" class="flex-1 py-2.5 border border-white/15 text-white/70 text-sm font-medium rounded-lg hover:bg-white/5 hover:border-white/25 transition-all inline-flex items-center justify-center gap-1.5">
                        <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-3.5 h-3.5" />
                        See top
                    </button>
                </div>
            </div>

            <!-- Related Auditions Grid -->
            <div v-if="moreAuditions.length > 0" class="py-6 border-t border-white/10">
                <h3 class="text-white font-bold text-sm mb-4">
                    More Auditions
                </h3>
                <div class="grid grid-cols-2 gap-3">
                    <Link
                        v-for="audition in moreAuditions"
                        :key="audition.id"
                        :href="isUserLoggedIn ?
                            route('members2.reels.feed', {
                                tag: 'auditions',
                                post: audition.slug
                            }) :
                            route(routePrefix + '.reels.feed', {
                                tag: 'auditions',
                                post: audition.slug
                            })"
                        class="group relative bg-zinc-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-white/20 transition-all text-left"
                    >
                        <!-- Thumbnail -->
                        <div class="relative w-full aspect-[3/4] bg-zinc-800">
                            <img v-if="requiresAgeVerification"
                                :src="audition.lock.start_card_video_images.image_600x1066_webp"
                                :alt="audition.title"
                                class="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-300"
                            />
                            <img v-else
                                :src="audition.start_card_images?.image_600x1066_webp"
                                :alt="audition.title"
                                class="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-300"
                            />

                            <!-- Status Badge -->
                            <div v-if="audition.is_near_goal" class="absolute top-2 left-2">
                                <div class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md min-h-[22px] text-[10px] leading-none font-semibold tracking-wide uppercase bg-black/55 backdrop-blur-md border border-amber-400/30 text-amber-200 shadow-sm">
                                    <TrendingUp class="w-3 h-3 opacity-90" />
                                    <span>Near Goal</span>
                                </div>
                            </div>

                            <!-- Progress Bar Overlay -->
                            <div v-if="(audition.is_new || audition.is_near_goal)" class="absolute bottom-0 left-0 right-0 px-2 pb-2">
                                <div class="relative h-1.5 bg-white/8 rounded-full overflow-hidden">
                                    <div
                                        class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full transition-all duration-300"
                                        :style="{ width: `${Math.min(getPercentage(audition), 100)}%` }"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Info -->
                        <div class="p-2">
                            <h4 class="text-white text-xs font-semibold line-clamp-2 leading-tight mb-1">
                                {{ audition.title }}
                            </h4>
                            <p class="text-gray-400 text-[12px] line-clamp-1">
                                {{ audition.creator.name }}
                            </p>
                        </div>
                    </Link>
                </div>
            </div>

            <!-- Footer Links -->
            <div class="py-6 mt-4 text-[11px] text-gray-500 space-y-2 border-t border-white/10">
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <a href="https://support.latouraineinc.com" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Support</a>
                    <span class="text-[#6d6d6d]">|</span>
                    <Link :href="route('www.main.privacyPolicy')" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Privacy Policy</Link>
                    <span class="text-[#6d6d6d]">|</span>
                    <Link :href="route('www.main.termsOfService')" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Terms of Service</Link>
                </div>
                <div class="flex flex-wrap items-center gap-x-1 gap-y-1">
                    <a href="https://www.epoch.com" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Epoch.com</a>
                    <span class="text-[#6d6d6d]">|</span>
                    <a href="https://segpaycs.com" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Segpay.com</a>
                </div>
                <div class="flex flex-wrap items-center gap-x-1 gap-y-1">
                    <Link :href="route('www.main.usc2257')" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">18 USC 2257 Record-Keeping Requirements Compliance Statement</Link>
                    <span class="text-[#6d6d6d]">|</span>
                    <Link :href="route('www.main.complianceMastercard')" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Compliance with MasterCard Standards</Link>
                </div>
                <p class="text-[10px]">&copy; {{ new Date().getFullYear() }} La Touraine, Inc.</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { Info, TrendingUp } from 'lucide-vue-next'
import KebabMenu from './KebabMenu.vue'
import {Link, router} from "@inertiajs/vue3";
import {getMainAuditionsUrl} from "@/Helpers/auditions";
import { trackFathomEvent, getFathomPostPrefix } from "@/Helpers/analytics";

const props = defineProps({
    videoData: {
        type: Object,
        default: () => ({})
    },
    moreAuditions: {
        type: Array,
    },
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    isGoldUser: {
        type: Boolean,
        default: false
    },
    routePrefix: {
        type: String
    },
    starBalance: {
        type: Number,
        default: 0
    },
    sponsorsData: {
        type: Object,
        default: () => ({ data: [], total: 0, current_page: 1, last_page: 1 })
    },
    requiresAgeVerification: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['joinClick', 'starClick', 'goldJoinClick', 'viewSponsors'])

const fathomPrefix = computed(() => getFathomPostPrefix(props.routePrefix))

const handleGiveStarsClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Sidebar_Give_Stars_Clicked`)
    emit('starClick')
}

const handleGetStarsClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Sidebar_Get_Stars_Clicked`)
    emit('joinClick')
}

const handleSeeCommentsClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Sidebar_See_Comments_Clicked`)
    emit('viewSponsors')
}

const handleSeeTopClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Sidebar_See_Top_Clicked`)
    emit('viewSponsors')
}

const creatorInitial = computed(() => props.videoData.creator.name?.[0] || '?')

const getPercentage = (audition) => {
    return (audition.likes / audition.star_goal) * 100
}

const handleImageError = (e) => {
    e.target.style.display = 'none'
    e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center text-white text-xl font-bold">${creatorInitial.value}</div>`
}

// --- Sponsorship widget ---
const sponsorPercentage = computed(() => {
    const likes = props.videoData.likes || 0
    const goal = props.videoData.star_goal || 1
    const percentage = Math.round((likes / goal) * 100)
    return percentage > 100 ? 100 : percentage
})

// Derive sidebar sponsor list from the API data
const sidebarSponsors = computed(() => {
    return (props.sponsorsData.data || []).slice(0, 5).map((s, index) => ({
        name: s.sponsor_name,
        amount: s.total_stars,
        label: index === 0 ? 'Top sponsor' : (index === 1 ? 'Recent sponsor' : null),
    }))
})
const sponsorCount = computed(() => props.sponsorsData.total || props.videoData?.unique_sponsors_count || 0)

// SVG progress ring calculations
const RING_RADIUS = 38
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS // ~238.76

const progressOffset = computed(() => {
    return RING_CIRCUMFERENCE * (1 - sponsorPercentage.value / 100)
})

const formatNumber = (num) => {
    if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
    }
    return num.toLocaleString()
}

const getSponsorInitial = (sponsor) => {
    return sponsor.name?.[0] || '?'
}

const goToMyProfile = (entry) => {
    const url = props.isUserLoggedIn ?
        route('members2.profile.my', { slug: entry.name }) :
        route(props.routePrefix + '.profile.my', { slug: entry.name })

    router.visit(url, {
        preserveScroll: false,
        preserveState: true,
    })
}
</script>
<style scoped>
.custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
    width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    transition: background-color 0.2s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.25);
}

.custom-scrollbar::-webkit-scrollbar-thumb:active {
    background-color: rgba(255, 255, 255, 0.35);
}
</style>
